import { Gift, Ticket, AlertCircle, Loader2 } from 'lucide-react'
import {
  useCustomerSpringbig,
  type SpringbigReward,
  type SpringbigOffer,
} from '@/api/customers'
import { usePOSStore } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import { useSelectCustomer } from '@/hooks'
import { formatCurrency } from '@/lib/utils'
import type { Customer } from '@/types'

// SpringBig's wire format for percentage offers/rewards. BE compares the
// payment's `discount_type` against this literal at frontend.py:599 to
// switch into percent-of-total math; anything else (or absent) is treated
// as a flat-dollar redemption. Exported so chip-display sites can branch
// on it without re-declaring the sentinel.
export const SPRINGBIG_PERCENT_TYPE = '%'

// Render-time helper. The dollar impact of a percent redemption depends
// on the live cart subtotal, so chip-display sites recompute on every
// render rather than caching a stale value at stage-time. The BE
// recomputes against the live receipt total at pay-time, so this stays
// just an FE estimate.
export const computePercentDollar = (percent: number, subtotal: number): number => {
  if (!Number.isFinite(percent) || percent <= 0) return 0
  if (!Number.isFinite(subtotal) || subtotal <= 0) return 0
  return Math.round(((subtotal * percent) / 100) * 100) / 100
}

// Shared rewards/offers picker. Used by both:
//   - LoyaltyPanel inside the customer modal (full-feature view with
//     balance cards, history, etc. — the picker is just one section).
//   - CheckoutModal as a sub-screen tied to the "Loyalty" payment tile,
//     so the cashier can pick a redemption without leaving checkout.
//
// The component owns its own springbig fetch + posStore staging — both
// consumers just hand it a customer and an `onApplied` callback.
export interface LoyaltyRedemptionPickerProps {
  customer: Customer
  // Called when the cashier toggles a redemption (stage or clear).
  // Consumers use this to close the parent surface (drawer / sub-screen)
  // after a successful pick.
  onApplied?: () => void
  // When true, the picker hides empty-state placeholders for sections
  // that have no rows. Used in CheckoutModal where vertical real estate
  // is tighter and an empty section is just visual noise.
  hideEmptySections?: boolean
}

export function LoyaltyRedemptionPicker({
  customer,
  onApplied,
  hideEmptySections = false,
}: LoyaltyRedemptionPickerProps) {
  const { data: springbig, isLoading, isError } = useCustomerSpringbig(customer.id)
  const pendingReward = usePOSStore((s) => s.pendingReward)
  const pendingOffer = usePOSStore((s) => s.pendingOffer)
  const setPendingReward = usePOSStore((s) => s.setPendingReward)
  const setPendingOffer = usePOSStore((s) => s.setPendingOffer)
  const selectedCustomer = usePOSStore((s) => s.selectedCustomer)
  const selectCustomer = useSelectCustomer()
  const showToast = useUIStore((s) => s.showToast)
  const cartDiscount = useCartStore((s) => s.discount)
  const setCartDiscount = useCartStore((s) => s.setDiscount)

  // Active customer must match the picker's target. Otherwise the staged
  // reward would be applied at pay-time against the wrong member.
  // Callback shape: `onReady` fires only when the customer is in place
  // -- either it was already correct, or the mutation succeeded. If the
  // mutation fails (e.g. partial-payment lock), onReady is NOT called,
  // so the surrounding flow (stage + success toast + onApplied) is
  // skipped. Without this gate the cashier would see both the lock
  // warning toast AND a "Reward staged" success toast simultaneously.
  const ensureCartCustomer = (onReady: () => void) => {
    if (selectedCustomer?.id === customer.id) {
      onReady()
      return
    }
    selectCustomer.mutate(customer, { onSuccess: onReady })
  }

  // "One loyalty redemption per sale." A percent redemption occupies the
  // cart-discount slot and carries a SpringBig id; a flat redemption stages
  // in pendingReward/pendingOffer. Both standing at once double-discounts the
  // sale -- the cart discount applies AND checkout still posts the type-299
  // (hasPendingLoyalty stays true). The guards below refuse a second source.
  // A plain MANUAL discount carries no id, so a manual discount + a flat
  // redemption (a discount plus a tender) is NOT a double redemption and
  // stays allowed.
  const percentRedemptionApplied = !!(
    cartDiscount && (cartDiscount.offer_id || cartDiscount.reward_id)
  )
  const blockSecondSource = () => {
    showToast({
      type: 'warning',
      title: 'One redemption per sale',
      message: 'Remove the current discount or redemption before applying another.',
    })
  }

  // Percent offers/rewards apply PRE-TAX as the cart discount, so tax is
  // computed on the reduced subtotal (a 10% reward IS 10% off the subtotal).
  // The SpringBig id rides the discount so the redemption is attributed at
  // commit. This is the only tax-correct path for a percent redemption --
  // flat-dollar redemptions stay POST-TAX type-299 payments (like a gift
  // card) and are staged below. One discount source per sale: refuse if any
  // other discount (a manual one, or a different redemption) holds the slot.
  const applyPercentRedemption = (
    ids: { offer_id?: number; reward_id?: number },
    isApplied: boolean,
    rawPercent: number,
    label: string,
  ) => {
    if (isApplied) {
      setCartDiscount(null)
      onApplied?.()
      return
    }
    if (rawPercent <= 0) {
      showToast({
        type: 'warning',
        title: 'Redemption unavailable',
        message: 'This redemption is missing a discount amount and cannot be applied.',
      })
      return
    }
    // Percent needs the cart-discount slot, so refuse if ANY discount holds
    // it (manual or another percent redemption); also refuse if a flat
    // redemption is already staged (would be a second redemption).
    if (cartDiscount || pendingReward || pendingOffer) {
      blockSecondSource()
      return
    }
    ensureCartCustomer(() => {
      setCartDiscount({ percent: rawPercent, reason_other: label, ...ids })
      showToast({
        type: 'success',
        title: 'Redemption applied',
        message: `${label}: ${rawPercent}% off the subtotal.`,
      })
      onApplied?.()
    })
  }

  const toggleReward = (reward: SpringbigReward) => {
    // Percent rewards ride the pre-tax cart discount; flat ones stage as a
    // post-tax payment.
    if (reward.discount_type === SPRINGBIG_PERCENT_TYPE) {
      applyPercentRedemption(
        { reward_id: reward.id },
        cartDiscount?.reward_id === reward.id,
        toNumber(reward.discount_amount),
        rewardLabel(reward),
      )
      return
    }
    if (pendingReward?.id === reward.id) {
      setPendingReward(null)
      onApplied?.()
      return
    }
    // One redemption per sale: refuse if a percent redemption is applied or
    // another flat redemption is already staged. (A manual discount alone
    // does not block -- discount + tender is allowed.)
    if (percentRedemptionApplied || pendingReward || pendingOffer) {
      blockSecondSource()
      return
    }
    const staged = buildStagedLoyalty(
      reward.id,
      reward.discount_amount,
      rewardLabel(reward),
    )
    if (!staged) {
      showToast({
        type: 'warning',
        title: 'Reward unavailable',
        message: 'This reward is missing a discount amount and cannot be applied.',
      })
      return
    }
    ensureCartCustomer(() => {
      setPendingReward(staged)
      showToast({
        type: 'success',
        title: 'Reward staged',
        message: `${rewardLabel(reward)} will apply at checkout.`,
      })
      onApplied?.()
    })
  }

  const toggleOffer = (offer: SpringbigOffer) => {
    // Percent offers ride the pre-tax cart discount; flat ones stage as a
    // post-tax payment.
    if (offer.discount_type === SPRINGBIG_PERCENT_TYPE) {
      applyPercentRedemption(
        { offer_id: offer.id },
        cartDiscount?.offer_id === offer.id,
        toNumber(offer.discount_amount),
        offerLabel(offer),
      )
      return
    }
    if (pendingOffer?.id === offer.id) {
      setPendingOffer(null)
      onApplied?.()
      return
    }
    // One redemption per sale: refuse if a percent redemption is applied or
    // another flat redemption is already staged. (A manual discount alone
    // does not block -- discount + tender is allowed.)
    if (percentRedemptionApplied || pendingReward || pendingOffer) {
      blockSecondSource()
      return
    }
    const staged = buildStagedLoyalty(
      offer.id,
      offer.discount_amount,
      offerLabel(offer),
    )
    if (!staged) {
      showToast({
        type: 'warning',
        title: 'Offer unavailable',
        message: 'This offer is missing a discount amount and cannot be applied.',
      })
      return
    }
    ensureCartCustomer(() => {
      setPendingOffer(staged)
      showToast({
        type: 'success',
        title: 'Offer staged',
        message: `${offerLabel(offer)} will apply at checkout.`,
      })
      onApplied?.()
    })
  }

  // Stage a FLAT-dollar redemption for checkout. Flat redemptions are
  // post-tax type-299 payments (like a gift card), so they're staged in
  // posStore and tendered at pay-time. PERCENT redemptions never reach here
  // -- they apply pre-tax via the cart discount (see applyPercentRedemption).
  // Returns null when the redemption has no usable dollar amount.
  function buildStagedLoyalty(
    id: number,
    discountAmount: number | string | undefined,
    label: string,
  ) {
    const rawAmount = toNumber(discountAmount)
    if (rawAmount <= 0) return null
    return { id, amount: rawAmount, label }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin mr-2" />
        Loading loyalty…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900">Loyalty data unavailable</p>
          <p className="text-xs text-amber-700 mt-0.5">
            SpringBig didn't respond. Try again later.
          </p>
        </div>
      </div>
    )
  }

  if (!springbig?.loyalty_enabled) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">SpringBig is not configured for this store.</p>
      </div>
    )
  }

  if (!springbig.customer_enrolled) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          This customer is not enrolled in the Points loyalty program.
        </p>
      </div>
    )
  }

  const rewards = springbig.rewards ?? []
  const offers = springbig.offers ?? []
  const showRewards = rewards.length > 0 || !hideEmptySections
  const showOffers = offers.length > 0 || !hideEmptySections

  return (
    <div className="space-y-3">
      {showRewards && (
        <Section icon={Gift} title={`Available rewards (${rewards.length})`}>
          {rewards.length === 0 ? (
            <p className="text-xs text-gray-400 px-2 py-3 bg-gray-50 rounded-lg">
              No rewards the customer can afford right now.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {rewards.map((reward) => (
                <RedemptionRow
                  key={reward.id}
                  title={rewardLabel(reward)}
                  subtitle={`${Math.round(toNumber(reward.item_value))} pts`}
                  amount={toNumber(reward.discount_amount)}
                  discountType={reward.discount_type}
                  isStaged={
                    reward.discount_type === SPRINGBIG_PERCENT_TYPE
                      ? cartDiscount?.reward_id === reward.id
                      : pendingReward?.id === reward.id
                  }
                  onToggle={() => toggleReward(reward)}
                />
              ))}
            </ul>
          )}
        </Section>
      )}

      {showOffers && (
        <Section icon={Ticket} title={`Available offers (${offers.length})`}>
          {offers.length === 0 ? (
            <p className="text-xs text-gray-400 px-2 py-3 bg-gray-50 rounded-lg">
              No active offers for this customer.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {offers.map((offer) => (
                <RedemptionRow
                  key={offer.id}
                  title={offerLabel(offer)}
                  subtitle={offerSubtitle(offer)}
                  amount={toNumber(offer.discount_amount)}
                  discountType={offer.discount_type}
                  isStaged={
                    offer.discount_type === SPRINGBIG_PERCENT_TYPE
                      ? cartDiscount?.offer_id === offer.id
                      : pendingOffer?.id === offer.id
                  }
                  onToggle={() => toggleOffer(offer)}
                />
              ))}
            </ul>
          )}
        </Section>
      )}

      {rewards.length === 0 && offers.length === 0 && hideEmptySections && (
        <div className="p-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-center text-sm text-gray-500">
          No redemptions available right now.
        </div>
      )}
    </div>
  )
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Gift
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        <Icon size={12} />
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

function RedemptionRow({
  title,
  subtitle,
  amount,
  discountType,
  isStaged,
  onToggle,
}: {
  title: string
  subtitle: string
  amount: number
  discountType?: string
  isStaged: boolean
  onToggle: () => void
}) {
  // SpringBig's discount_amount carries the percent (e.g. 10 for 10% off)
  // when discount_type is '%'. Rendering that as `$10 off` mis-states the
  // redemption to the cashier; the actual dollar discount depends on the
  // cart total at pay-time. Format as `10% off` instead.
  const amountLabel =
    amount > 0
      ? discountType === SPRINGBIG_PERCENT_TYPE
        ? `${amount}% off`
        : `${formatCurrency(amount)} off`
      : ''
  return (
    <li>
      <div
        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors ${
          isStaged
            ? 'bg-green-50 border-green-200'
            : 'bg-gray-50 border-transparent'
        }`}
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
          <p className="text-[11px] text-gray-500 truncate">
            {subtitle}
            {amountLabel ? ` · ${amountLabel}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className={`shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer ${
            isStaged
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
          }`}
        >
          {isStaged ? 'Clear' : 'Apply at checkout'}
        </button>
      </div>
    </li>
  )
}

// SpringBig pass-through fields aren't strongly typed — BE forwards
// them verbatim. These read whichever name the API happens to populate.
export function rewardLabel(reward: SpringbigReward): string {
  return (
    pickString(reward, ['title', 'name', 'description', 'reward_name'])
    ?? `Reward #${reward.id}`
  )
}

export function offerLabel(offer: SpringbigOffer): string {
  return (
    pickString(offer, ['title', 'name', 'description', 'offer_name'])
    ?? `Offer #${offer.id}`
  )
}

function offerSubtitle(offer: SpringbigOffer): string {
  const left = offer.redemptions_available
  const exp = offer.expiration ? ` · expires ${formatShortDate(offer.expiration)}` : ''
  return `${left} use${left === 1 ? '' : 's'} left${exp}`
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v
  }
  return null
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
